import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, FileText, ArrowRight } from 'lucide-react';
import { Button } from './ui/Button';
export function LabTesting() {
  return <section id="lab" className="w-full py-20 lg:py-28 bg-foreground text-background" data-id="element-331">
      <div className="container mx-auto" data-id="element-332">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-333">
          <motion.div initial={{
          opacity: 0,
          y: 14
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.5
        }} data-id="element-334">
            <p className="text-[11px] uppercase tracking-wider text-background/60 font-medium mb-3" data-id="element-335">
              Lab testing
            </p>
            <h2 className="text-3xl lg:text-5xl font-semibold tracking-[-0.025em] text-background" data-id="element-336">
              Every batch. Every peptide. Independently verified.
            </h2>
            <p className="mt-6 text-[16px] leading-relaxed text-background/70 max-w-lg" data-id="element-337">
              We send every batch to an ISO-accredited third-party laboratory
              for HPLC analysis. The certificate is published before the product
              goes on sale.
            </p>
            <ul className="mt-8 space-y-3" data-id="element-338">
              {['HPLC and mass spectrometry per batch', 'ISO 17025 accredited laboratory', 'Certificate of Analysis published publicly', 'Batch number printed on every vial'].map(item => <li key={item} className="flex items-start gap-3 text-background/85" data-id="element-339">
                  <CheckCircle2 className="h-4 w-4 mt-1 text-primary flex-shrink-0" strokeWidth={2} data-id="element-340" />
                  <span className="text-sm" data-id="element-341">{item}</span>
                </li>)}
            </ul>
            <div className="mt-8" data-id="element-342">
              <Button variant="secondary" className="rounded-full bg-background text-foreground hover:bg-background/90" data-id="element-343">
                View sample COA
                <ArrowRight className="h-4 w-4 ml-1" strokeWidth={2} data-id="element-344" />
              </Button>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 14
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="relative" data-id="element-345">
            <div className="rounded-2xl bg-background text-foreground p-7 lg:p-9 border border-background/10" data-id="element-346">
              <div className="flex items-center justify-between pb-5 border-b border-border" data-id="element-347">
                <div className="flex items-center gap-3" data-id="element-348">
                  <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center" data-id="element-349">
                    <FileText className="h-4 w-4 text-primary" strokeWidth={1.75} data-id="element-350" />
                  </div>
                  <div data-id="element-351">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-352">
                      Certificate of Analysis
                    </div>
                    <div className="text-sm font-semibold" data-id="element-353">
                      BPC-157 · Batch BP-24091
                    </div>
                  </div>
                </div>
                <span className="font-mono text-[11px] text-muted-foreground" data-id="element-354">
                  12 Apr 2026
                </span>
              </div>

              <div className="grid grid-cols-2 gap-5 py-6 border-b border-border" data-id="element-355">
                <div data-id="element-356">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-357">
                    Purity (HPLC)
                  </div>
                  <div className="font-mono text-2xl text-foreground mt-1 font-medium" data-id="element-358">
                    99.7%
                  </div>
                </div>
                <div data-id="element-359">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-360">
                    Net peptide content
                  </div>
                  <div className="font-mono text-2xl text-foreground mt-1 font-medium" data-id="element-361">
                    5.02mg
                  </div>
                </div>
                <div data-id="element-362">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-363">
                    Mass (M+H)
                  </div>
                  <div className="font-mono text-base text-foreground mt-1" data-id="element-364">
                    1419.6
                  </div>
                </div>
                <div data-id="element-365">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-366">
                    Endotoxin
                  </div>
                  <div className="font-mono text-base text-foreground mt-1" data-id="element-367">
                    &lt; 0.05 EU/mg
                  </div>
                </div>
              </div>

              {/* Chromatogram */}
              <div className="pt-6" data-id="element-368">
                <div className="flex items-center justify-between mb-3" data-id="element-369">
                  <span className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-370">
                    HPLC chromatogram
                  </span>
                  <span className="font-mono text-[10px] text-muted-foreground" data-id="element-371">
                    Retention 8.41 min
                  </span>
                </div>
                <svg viewBox="0 0 400 100" className="w-full h-24" preserveAspectRatio="none" data-id="element-372">
                  <defs data-id="element-373">
                    <pattern id="grid" width="40" height="20" patternUnits="userSpaceOnUse" data-id="element-374">
                      <path d="M 40 0 L 0 0 0 20" fill="none" stroke="currentColor" strokeOpacity="0.08" strokeWidth="1" data-id="element-375" />
                    </pattern>
                  </defs>
                  <rect width="400" height="100" fill="url(#grid)" className="text-foreground" data-id="element-376" />
                  <path d="M0,90 L60,90 L80,88 L120,86 L150,84 L170,80 L185,60 L195,15 L205,15 L215,55 L230,82 L260,86 L300,88 L340,89 L400,90" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-primary" data-id="element-377" />
                </svg>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}